import type { BrowserAdapters, SessionResponse, ActionConfirmation, ActionResult, Delegation, BackendEvent, SessionStatus, VoiceError, VoiceErrorCode, TranscriptSegment } from './types';
import { VoiceApi, ApiError } from './api';

const ICE_TIMEOUT_MS = 3000;
const SESSION_START_TIMEOUT_MS = 10000;
// Backend finalization allows 10 seconds; include 2 seconds for delivery.
const GRACEFUL_CLOSE_TIMEOUT_MS = 12000;

export interface VoiceState {
  status: SessionStatus;
  muted: boolean;
  captions: TranscriptSegment[];
  pending: ActionConfirmation[];
  delegations: Delegation[];
  usageSeconds: number;
  costUsd: number | null;
  costFinal: boolean;
  conversationId?: string;
  error?: VoiceError;
  closeReason?: string;
}

interface VoiceSessionOptions {
  api: VoiceApi;
  adapters: BrowserAdapters;
  conversationId?: string;
  selectedDelegationId?: string;
  inputStream?: MediaStream;
  onChange: (state: VoiceState) => void;
  onError?: (err: VoiceError) => void;
  onAudioBlocked?: () => void;
}

function emptyState(status: SessionStatus): VoiceState {
  return {
    status,
    muted: false,
    captions: [],
    pending: [],
    delegations: [],
    usageSeconds: 0,
    costUsd: null,
    costFinal: false,
  };
}

class CancelledError extends Error {
  constructor() {
    super('cancelled');
  }
}

export class VoiceSession {
  private pc?: RTCPeerConnection;
  private dc?: RTCDataChannel;
  private ws?: WebSocket;
  private localStream?: MediaStream;
  private current?: SessionResponse;
  private state: VoiceState;
  private iceResolve?: () => void;
  private iceTimer?: ReturnType<typeof setTimeout>;
  private startTimer?: ReturnType<typeof setTimeout>;
  private closeTimer?: ReturnType<typeof setTimeout>;
  private disposed = false;
  private generation = 0;
  private currentGen?: number;
  private started = false;
  private closing = false;
  private closeResolve?: () => void;
  private closePromise?: Promise<void>;
  private remoteAudio?: HTMLAudioElement;
  private awaitingBackendCloseReason = false;

  constructor(private opts: VoiceSessionOptions) {
    this.state = emptyState('idle');
    this.localStream = opts.inputStream;
  }

  getState(): VoiceState {
    return this.state;
  }

  getConversationId(): string | undefined {
    return this.current?.conversation_id ?? this.opts.conversationId;
  }

  async start(takeover = false): Promise<void> {
    if (this.state.status !== 'idle') {
      throw new Error('VoiceSession already active');
    }
    const startGen = ++this.generation;
    this.currentGen = startGen;
    this.started = false;
    this.update('starting');
    try {
      this.pc = new this.opts.adapters.RTCPeerConnection({ iceServers: [] });
      this.pc.onconnectionstatechange = () => this.onConnectionStateChange();
      this.pc.oniceconnectionstatechange = () => this.onIceConnectionStateChange();
      this.pc.ontrack = (ev) => this.onRemoteTrack(ev);

      this.dc = this.pc.createDataChannel('oai-events', { ordered: true });
      this.dc.onopen = () => { /* no-op */ };
      this.dc.onmessage = (ev) => this.handleDataChannel(ev);
      this.dc.onerror = () => {
        if (this.inGen(startGen)) this.onError('webrtc', 'Data channel error', true);
      };

      const stream = this.opts.inputStream ?? await this.opts.adapters.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
      if (!this.inGen(startGen)) {
        this.stopLateStream(stream);
        return;
      }
      this.localStream = stream;
      this.localStream.getAudioTracks().forEach((track) => this.pc!.addTrack(track, this.localStream!));

      const offer = await this.pc.createOffer();
      if (!this.inGen(startGen)) return;
      await this.pc.setLocalDescription(offer);
      if (!this.inGen(startGen)) return;

      await this.gatherIce(startGen);
      if (!this.inGen(startGen)) return;

      const sdp = this.pc.localDescription?.sdp;
      if (!sdp) {
        throw new Error('No local SDP after ICE gathering');
      }
      if (!this.inGen(startGen)) return;

      if (!this.started) {
        this.update('awaiting_answer');
      }
      const createPayload: import('./types').CreateSessionRequest = { sdp };
      if (this.opts.conversationId) createPayload.conversation_id = this.opts.conversationId;
      if (this.opts.selectedDelegationId) createPayload.selected_delegation_id = this.opts.selectedDelegationId;
      if (takeover) createPayload.takeover = true;

      const created = await this.opts.api.createSession(createPayload);
      if (!this.inGen(startGen)) {
        this.stopLateStream(this.localStream);
        void this.closeBackendSession(created);
        return;
      }
      this.current = created;
      this.state.conversationId = created.conversation_id;

      await this.pc.setRemoteDescription({ type: 'answer', sdp: this.current.transport.sdp });
      if (!this.inGen(startGen)) {
        this.stopLateStream(this.localStream);
        void this.closeBackendSession(this.current);
        return;
      }

      const socket = this.opts.api.eventsSocket(this.current.session.id);
      socket.onopen = () => { /* no-op */ };
      socket.onmessage = (ev) => this.handleBackendEvent(ev);
      socket.onerror = () => {
        if (!this.disposed) void this.stop();
      };
      socket.onclose = () => {
        // Socket loss is not a final usage receipt. The authenticated close
        // request can still reconcile the session while WebRTC stays alive.
        if (!this.disposed) void this.stop();
      };
      this.ws = socket;

      if (!this.started) {
        this.startTimer = setTimeout(() => {
          if (this.inGen(startGen) && !this.started) {
            this.onError('session', 'Session did not start in time', true);
          }
        }, SESSION_START_TIMEOUT_MS);
      }
    } catch (err) {
      if (err instanceof CancelledError) {
        this.cleanup();
        return;
      }
      this.onUnknownError(err);
    }
  }

  setMuted(muted: boolean): void {
    this.localStream?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
    this.state.muted = muted;
    this.emit();
  }

  async stop(): Promise<void> {
    if (this.state.status === 'idle' || this.state.status === 'closed' || this.state.status === 'error') {
      return;
    }
    if (this.closing) {
      return this.closePromise ?? Promise.resolve();
    }
    this.closing = true;
    this.currentGen = undefined;

    if (this.current) {
      if (this.startTimer) { clearTimeout(this.startTimer); this.startTimer = undefined; }
      this.update('closing');
      this.closePromise = new Promise<void>((resolve) => {
        this.closeResolve = resolve;
      });
      this.closeTimer = setTimeout(() => {
        if (this.closing) this.onRemoteClose('close timeout');
      }, GRACEFUL_CLOSE_TIMEOUT_MS);
      // The local deadline owns completion even if fetch never settles.
      void this.closeBackendSession(this.current, true);
      return this.closePromise;
    }

    void this.closeBackendSession(this.current);
    this.cleanup();
    this.update('closed');
    this.closing = false;
    this.closeResolve?.();
    this.closeResolve = undefined;
    this.closePromise = undefined;
  }

  async confirm(action: ActionConfirmation): Promise<void> {
    if (!this.current) return;
    this.setActionBusy(action.id, true);
    try {
      const res = await this.opts.api.confirmAction(action.id, action.manifest_hash, this.current.epoch);
      this.setActionResult(action.id, res.action);
    } catch (err) {
      // A lost response cannot prove the server did not execute the action.
      this.setActionResult(action.id, { id: action.id, status: 'UNKNOWN' });
      if (err instanceof ApiError && [401, 403].includes(err.status)) {
        this.onError('auth', 'Your Slack authorization is no longer available. Sign in again.', false);
      }
    }
  }

  async cancel(action: ActionConfirmation): Promise<void> {
    if (!this.current) return;
    this.setActionBusy(action.id, true);
    try {
      const res = await this.opts.api.cancelAction(action.id, action.manifest_hash, this.current.epoch);
      this.setActionResult(action.id, res.action);
    } catch (err) {
      // A lost response cannot prove the server did not execute the action.
      this.setActionResult(action.id, { id: action.id, status: 'UNKNOWN' });
      if (err instanceof ApiError && [401, 403].includes(err.status)) {
        this.onError('auth', 'Your Slack authorization is no longer available. Sign in again.', false);
      }
    }
  }

  resumeAudio(): void {
    this.remoteAudio
      ?.play()
      .then(() => { /* no-op */ })
      .catch(() => {
        this.opts.onAudioBlocked?.();
      });
  }

  dispose(): void {
    void this.closeBackendSession(this.current);
    this.cleanup();
    this.update('closed');
  }

  private inGen(startGen: number): boolean {
    return !this.disposed && this.currentGen === startGen;
  }

  private stopLateStream(stream?: MediaStream): void {
    stream?.getAudioTracks().forEach((track) => track.stop());
  }

  private async closeBackendSession(response?: SessionResponse, finalizeCurrent = false): Promise<void> {
    if (!response) return;
    try {
      const result = await this.opts.api.closeSession(response.session.id);
      if (finalizeCurrent && !this.disposed && this.current?.session.id === response.session.id && result.finalized) {
        if (typeof result.usage_seconds === 'number') this.applyUsage(result.usage_seconds, true);
        // A provider-initiated close may be an idle timeout. Its authenticated
        // backend event owns that reason; a racing generic HTTP ACK must not
        // erase it before the hands-free controller can safely rearm.
        if (!this.awaitingBackendCloseReason) this.onRemoteClose('backend finalized');
      }
    } catch {
      // best effort; never let a billing session leak because of UI state
    }
  }

  private gatherIce(startGen: number): Promise<void> {
    return new Promise((resolve) => {
      this.iceResolve = resolve;
      if (!this.pc || !this.inGen(startGen)) {
        resolve();
        return;
      }
      if (this.pc.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      const handler = () => {
        if (this.pc?.iceGatheringState === 'complete') {
          this.pc.onicegatheringstatechange = null;
          resolve();
        }
      };
      this.pc.onicegatheringstatechange = handler;
      this.iceTimer = setTimeout(() => {
        if (this.pc) this.pc.onicegatheringstatechange = null;
        resolve();
      }, ICE_TIMEOUT_MS);
    });
  }

  private onConnectionStateChange(): void {
    if (this.disposed || this.closing || this.state.status === 'closed' || this.state.status === 'error') return;
    const state = this.pc?.connectionState;
    if (state === 'failed' || state === 'closed' || state === 'disconnected') {
      this.onError('webrtc', `Connection ${state}`, true);
    }
  }

  private onIceConnectionStateChange(): void {
    if (this.disposed || this.closing || this.state.status === 'closed' || this.state.status === 'error') return;
    if (this.pc?.iceConnectionState === 'failed') {
      this.onError('webrtc', 'ICE failed', true);
    }
  }

  private onRemoteTrack(ev: RTCTrackEvent): void {
    if (this.disposed || this.closing) return;
    const stream = ev.streams?.[0];
    if (!stream) return;
    if (!this.remoteAudio) {
      this.remoteAudio = this.opts.adapters.createAudio();
      this.remoteAudio.autoplay = true;
      this.remoteAudio.setAttribute('playsinline', '');
    }
    this.remoteAudio.srcObject = stream;
    this.remoteAudio
      .play()
      .then(() => { /* no-op */ })
      .catch(() => {
        this.opts.onAudioBlocked?.();
      });
  }

  private handleDataChannel(ev: MessageEvent): void {
    if (this.disposed) return;
    try {
      const msg = JSON.parse(ev.data);
      if (msg?.type === 'session.started') {
        this.markStarted();
      } else if (msg?.type === 'session.closed') {
        this.awaitingBackendCloseReason = true;
        const seconds = msg?.usage?.seconds;
        if (typeof seconds === 'number') {
          this.applyUsage(seconds, false);
        }
        // The backend must persist final accounting before we release its
        // event socket. Provider data-channel closure alone is not that ACK.
        void this.stop();
      }
      // Intentionally ignore OpenAI input/output transcript deltas; the backend
      // maps transcript events onto the WebSocket channel.
    } catch {
      // malformed data channel message; ignore
    }
  }

  private markStarted(): void {
    if (this.disposed || this.closing) return;
    this.started = true;
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = undefined;
    }
    if (this.state.status !== 'ready') {
      this.update('ready');
    }
  }

  private handleBackendEvent(ev: MessageEvent): void {
    if (this.disposed) return;
    try {
      const e = JSON.parse(ev.data) as BackendEvent;
      switch (e.type) {
        case 'status':
          // Backend status messages are for diagnostics; readiness is gated by
          // the oai-events data channel `session.started` event.
          break;
        case 'transcript':
          this.addTranscript(e.speaker, e.text, e.start_ms, e.end_ms);
          break;
        case 'confirmation':
          this.upsertAction(e.action);
          break;
        case 'delegation':
          this.upsertDelegation(e.delegation);
          break;
        case 'usage':
          this.applyUsage(e.seconds, e.final);
          break;
        case 'closed':
          this.onRemoteClose(e.reason);
          break;
        case 'error':
          this.onError(e.code as VoiceErrorCode, e.message, true);
          break;
      }
    } catch {
      // malformed backend event; ignore
    }
  }

  private addTranscript(speaker: string, text: string, start_ms: number, end_ms: number): void {
    const safeSpeaker = speaker === 'user' ? 'user' : 'assistant';
    const last = this.state.captions[this.state.captions.length - 1];
    if (last && last.speaker === safeSpeaker && last.start_ms === start_ms) {
      const next = [...this.state.captions];
      next[next.length - 1] = { ...last, text, end_ms };
      this.state.captions = next;
    } else {
      this.state.captions = [...this.state.captions, { speaker: safeSpeaker, text, start_ms, end_ms }];
    }
    this.emit();
  }

  private upsertAction(action: ActionConfirmation): void {
    const next = [...this.state.pending];
    const idx = next.findIndex((a) => a.id === action.id);
    const existing = idx >= 0 ? next[idx] : undefined;
    next[idx >= 0 ? idx : next.length] = { ...existing, ...action };
    this.state.pending = next;
    this.emit();
  }

  private upsertDelegation(delegation: Delegation): void {
    const next = [...this.state.delegations];
    const idx = next.findIndex((d) => d.id === delegation.id);
    next[idx >= 0 ? idx : next.length] = { ...next[idx], ...delegation };
    this.state.delegations = next;
    this.emit();
  }

  private applyUsage(seconds: number, final: boolean): void {
    if (!Number.isFinite(seconds) || seconds < 0 || (this.state.costFinal && !final)) return;
    this.state.usageSeconds = final ? seconds : Math.max(this.state.usageSeconds, seconds);
    this.state.costUsd = null; // Duration alone is not a verified billing price.
    this.state.costFinal = final;
    this.emit();
  }

  private setActionBusy(id: string, busy: boolean): void {
    this.state.pending = this.state.pending.map((a) => (a.id === id ? { ...a, busy } : a));
    this.emit();
  }

  private setActionResult(id: string, result: ActionResult): void {
    this.state.pending = this.state.pending.map((a) => (a.id === id ? { ...a, result, busy: false } : a));
    this.emit();
  }

  private onRemoteClose(reason: string): void {
    if (this.state.status === 'closed' || this.state.status === 'error') return;
    this.state.closeReason = reason;
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
    this.cleanup();
    this.update('closed');
    this.closeResolve?.();
    this.closeResolve = undefined;
    this.closePromise = undefined;
    this.closing = false;
  }

  private onError(code: VoiceErrorCode, message: string, recoverable: boolean): void {
    if (this.state.status === 'closed' || this.state.status === 'error') return;
    this.closeBackendSession(this.current).catch(() => { /* no-op */ });
    this.cleanup();
    this.state.error = { code, message, recoverable };
    this.update('error');
    this.opts.onError?.({ code, message, recoverable });
  }

  private onUnknownError(err: unknown): void {
    if (err instanceof CancelledError) {
      this.cleanup();
      return;
    }
    const message = err instanceof Error ? err.message : 'Unknown voice error';
    let code: VoiceErrorCode = 'unknown';
    if (err instanceof ApiError && err.code === 'unknown_provider_outcome') {
      code = 'unknown_provider_outcome';
    } else if (err instanceof ApiError && err.status === 409) {
      code = 'conflict';
    } else if (err instanceof ApiError && err.status === 401) {
      code = 'auth';
    } else if (message.toLowerCase().includes('notallowederror') || message.toLowerCase().includes('permission')) {
      code = 'mic_denied';
    } else if (message.toLowerCase().includes('notfounderror') || message.toLowerCase().includes('not found')) {
      code = 'mic_not_found';
    } else if (message.toLowerCase().includes('network') || message.toLowerCase().includes('fetch')) {
      code = 'network';
    }
    this.onError(code, message, true);
  }

  private cleanup(): void {
    this.disposed = true;
    this.iceResolve?.();
    this.iceResolve = undefined;
    this.closeResolve?.();
    this.closeResolve = undefined;
    this.currentGen = undefined;
    if (this.iceTimer) { clearTimeout(this.iceTimer); this.iceTimer = undefined; }
    if (this.startTimer) { clearTimeout(this.startTimer); this.startTimer = undefined; }
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = undefined; }
    this.localStream?.getAudioTracks().forEach((track) => track.stop());
    if (this.pc) {
      this.pc.onconnectionstatechange = null;
      this.pc.oniceconnectionstatechange = null;
      this.pc.ontrack = null;
      this.pc.onicegatheringstatechange = null;
      try { this.pc.close(); } catch { /* no-op */ }
      this.pc = undefined;
    }
    if (this.dc) {
      this.dc.onopen = null;
      this.dc.onmessage = null;
      this.dc.onerror = null;
      try { this.dc.close(); } catch { /* no-op */ }
      this.dc = undefined;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      try { this.ws.close(); } catch { /* no-op */ }
      this.ws = undefined;
    }
    if (this.remoteAudio) {
      this.remoteAudio.pause();
      this.remoteAudio.srcObject = null;
      this.remoteAudio = undefined;
    }
    this.localStream = undefined;
    this.current = undefined;
    this.started = false;
  }

  private update(status: SessionStatus): void {
    this.state.status = status;
    this.emit();
  }

  private emit(): void {
    this.opts.onChange({ ...this.state });
  }
}
